import { useEffect, useRef, useState } from "react";
import { Play, Pause, SkipBack, SkipForward, Film } from "lucide-react";
import { fileUrl } from "@/lib/api";
import { cn } from "@/lib/utils";

// Instant in-browser preview: plays each trimmed segment back-to-back (no render needed).
export function ReelPreview({ segments, clipsById, aspect, onDuration }) {
    const vref = useRef(null);
    const [idx, setIdx] = useState(0);
    const [playing, setPlaying] = useState(false);

    const seg = segments[idx];
    const clip = seg ? clipsById[seg.clip_id] : null;
    const portrait = aspect === "9:16";

    useEffect(() => { setIdx(0); }, [segments.length]);

    useEffect(() => {
        const v = vref.current;
        if (!v || !seg) return;
        const onMeta = () => {
            onDuration?.(seg.clip_id, v.duration);
            v.currentTime = Math.min(seg.in_sec || 0, v.duration - 0.05);
            if (playing) v.play().catch(() => {});
        };
        const onTime = () => {
            if (v.currentTime >= (seg.out_sec || v.duration)) advance();
        };
        v.addEventListener("loadedmetadata", onMeta);
        v.addEventListener("timeupdate", onTime);
        return () => { v.removeEventListener("loadedmetadata", onMeta); v.removeEventListener("timeupdate", onTime); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, seg?.clip_id, seg?.in_sec, seg?.out_sec, playing]);

    const advance = () => {
        if (idx < segments.length - 1) setIdx(idx + 1);
        else { setPlaying(false); setIdx(0); }
    };
    const toggle = () => {
        const v = vref.current;
        if (!v) return;
        if (playing) { v.pause(); setPlaying(false); }
        else { v.currentTime = Math.max(v.currentTime, seg?.in_sec || 0); v.play().then(() => setPlaying(true)).catch(() => {}); }
    };

    return (
        <div className="bg-surface border border-border rounded-sm p-4" data-testid="reel-preview">
            <div className={cn("mx-auto bg-obsidian rounded-sm overflow-hidden relative grid place-items-center",
                portrait ? "aspect-[9/16] max-w-[280px]" : "aspect-video w-full")}>
                {clip
                    ? <video key={`${seg.clip_id}-${idx}`} ref={vref} src={fileUrl(clip.video_media_url)} className="w-full h-full object-contain" playsInline muted={false} />
                    : <div className="text-center text-muted-foreground"><Film className="w-8 h-8 mx-auto mb-2" /><p className="text-xs font-mono">Add clips to preview your reel</p></div>}
                {clip && seg?.caption && (
                    <div className="absolute bottom-[12%] inset-x-0 px-4 text-center pointer-events-none">
                        <span className="inline-block bg-black/65 text-white text-sm font-semibold px-3 py-1 rounded-sm">{seg.caption}</span>
                    </div>
                )}
            </div>
            {segments.length > 0 && (
                <div className="flex items-center justify-center gap-4 mt-3">
                    <button onClick={() => setIdx(Math.max(0, idx - 1))} className="text-muted-foreground hover:text-white" data-testid="preview-prev"><SkipBack className="w-4 h-4" /></button>
                    <button onClick={toggle} data-testid="preview-play" className="grid place-items-center w-10 h-10 rounded-full bg-volt text-black hover:bg-[#33ceff] transition-colors">
                        {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                    </button>
                    <button onClick={advance} className="text-muted-foreground hover:text-white" data-testid="preview-next"><SkipForward className="w-4 h-4" /></button>
                    <span className="text-[11px] font-mono text-muted-foreground ml-2">Clip {idx + 1}/{segments.length}</span>
                </div>
            )}
        </div>
    );
}
